// HeroBanner INTEGRADO - Con flyer-1.jpg incluido
// Sin componentes externos, todo integrado

import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../supabaseClient';

const HeroBanner = ({ onPurchaseClick, isRaffleActive = true }) => {
  const [parallaxOffset, setParallaxOffset] = useState(0);
  
  // Estados para datos del sorteo
  const [raffleData, setRaffleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flyerLoaded, setFlyerLoaded] = useState(false);

  // Efecto Parallax
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.pageYOffset;
      const parallax = scrolled * 0.5;
      setParallaxOffset(parallax);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Efecto para cargar los datos del sorteo activo al montar el componente
  useEffect(() => {
    const fetchRaffleData = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('raffles')
          .select('*')
          .eq('estado', 'activo')
          .single();

        if (error) throw error;

        setRaffleData(data);
      } catch (err) {
        console.error('Error al cargar los datos del sorteo para el banner:', err);
        setError(err.message || 'Error desconocido');
      } finally {
        setLoading(false);
      }
    };

    fetchRaffleData();
  }, []);

  // Estados de error y carga
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-xl text-error">⚠️ Error al cargar la información del sorteo para el banner.</p>
        <p className="mt-2 text-sm text-gray-600">{error}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-secondary mx-auto"></div>
        <p className="mt-4 text-lg">Cargando banner del sorteo...</p>
      </div>
    );
  }

  if (!raffleData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-xl text-warning">ℹ️ No hay ningún sorteo activo en este momento.</p>
      </div>
    );
  }

  // ASIGNACIÓN DE DATOS DINÁMICOS
  const displayData = {
    title: raffleData?.titulo || "Gran Premio Áurea Fortuna 2024",
    mainPrize: raffleData?.premio_principal || "Bs. 0.00",
    secondPrize: raffleData?.premio_secundario || "Bs. 0.00",
    thirdPrize: raffleData?.premio_tercero || "Bs. 0.00",
    drawDate: raffleData?.fecha_sorteo ? new Date(raffleData.fecha_sorteo).toLocaleDateString('es-VE', { day: 'numeric', month: 'long', year: 'numeric' }) : "Fecha Desconocida",
    ticketPrice: raffleData?.precio_boleto || 50,
    // PRIORIDAD: flyer-1.jpg primero, luego fallback a Supabase
    image: '/assets/flyers/flyer-1.jpg', // 🎯 FLYER PRINCIPAL
    fallbackImage: raffleData?.imagen_url || "https://images.unsplash.com/photo-1699817586371-96a3837f0c97",
    imageAlt: "Imagen del Premio Principal"
  };

  // CÁLCULO DE ESTADÍSTICAS
  const ticketsSold = raffleData?.boletos_vendidos || 0;
  const totalTickets = raffleData?.boletos_totales || 1000;
  const availableTickets = totalTickets - ticketsSold;

  // RENDERIZACIÓN MEJORADA
  return (
    <div className="relative min-h-screen bg-gradient-to-br from-background via-navy/90 to-background overflow-hidden">
      {/* Parallax Background */}
      <div
        className="absolute inset-0 opacity-20"
        style={{ transform: `translateY(${parallaxOffset}px)` }}>
        <div className="absolute inset-0 bg-gradient-to-r from-secondary/30 via-transparent to-golden/30"></div>
        <div className="absolute top-20 left-10 w-32 h-32 bg-secondary/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-golden/20 rounded-full blur-3xl"></div>
      </div>
      
      {/* Content Container */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 lg:px-6 pt-20 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[calc(100vh-8rem)]">
          
          {/* Left Content - Información Principal */}
          <div className="text-center lg:text-left space-y-8">
            
            {/* Main Title */}
            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground leading-tight">
                {displayData?.title}
              </h1>
              <p className="text-xl md:text-2xl text-text-secondary font-body">
                Tu oportunidad de ganar premios increíbles con la lotería más confiable de Venezuela
              </p>
            </div>

            {/* Premio Principal Destacado */}
            <div className="bg-gradient-to-br from-card/90 to-card/70 backdrop-blur-sm rounded-3xl p-8 border border-golden/30 shadow-2xl relative overflow-hidden">
              {/* Efecto de brillo */}
              <div className="absolute inset-0 bg-gradient-to-r from-secondary/5 via-transparent to-golden/5"></div>
              
              <div className="relative text-center space-y-6">
                <div className="flex items-center justify-center space-x-3 mb-6">
                  <Icon name="Trophy" size={32} className="text-secondary" />
                  <span className="text-2xl font-heading font-bold text-foreground">
                    Gran Premio
                  </span>
                </div>
                
                {/* Premio Principal - Más prominente */}
                <div className="space-y-2">
                  <div className="text-5xl md:text-7xl font-heading font-bold text-secondary drop-shadow-lg">
                    {displayData?.mainPrize}
                  </div>
                  <div className="text-lg text-text-secondary font-medium">
                    Premio Principal
                  </div>
                </div>
                
                {/* Premios Secundarios - Diseño más limpio */}
                <div className="grid grid-cols-2 gap-6 pt-6 border-t border-golden/20">
                  <div className="text-center space-y-1">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-white"> 
                      {displayData?.secondPrize}
                    </div>
                    <div className="text-sm text-text-secondary font-medium">
                      2° Premio
                    </div>
                  </div>
                  <div className="text-center space-y-1">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-white"> 
                      {displayData?.thirdPrize}
                    </div>
                    <div className="text-sm text-text-secondary font-medium">
                      3° Premio
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Información del Sorteo */}
            <div className="space-y-6">
              <div className="flex items-center justify-center lg:justify-start space-x-6 text-text-secondary">
                <div className="flex items-center space-x-2 bg-card/50 rounded-full px-4 py-2">
                  <Icon name="Calendar" size={18} />
                  <span className="font-medium">{displayData?.drawDate}</span>
                </div>
                <div className="flex items-center space-x-2 bg-card/50 rounded-full px-4 py-2">
                  <Icon name="Ticket" size={18} />
                  <span className="font-medium">Bs. {displayData?.ticketPrice?.toLocaleString()}</span>
                </div>
                <div className="flex items-center space-x-2 bg-card/50 rounded-full px-4 py-2">
                  <Icon name="Package" size={18} />
                  <span className="font-medium">{availableTickets.toLocaleString()} disponibles</span>
                </div>
              </div>

              <Button
                variant="secondary"
                size="lg"
                onClick={onPurchaseClick}
                disabled={!isRaffleActive}
                iconName="ShoppingCart"
                iconPosition="left"
                className="w-full sm:w-auto text-xl px-12 py-5 bg-gradient-to-r from-secondary to-amber-500 hover:from-secondary/90 hover:to-amber-400 text-gray-900 font-bold rounded-2xl shadow-2xl transform hover:scale-105 transition-all duration-300">

                {isRaffleActive ? 'Comprar Boletos Ahora' : 'Sorteo Finalizado'}
              </Button>

              <p className="text-sm text-text-secondary font-medium max-w-lg mx-auto lg:mx-0">
                * Sorteo sujeto a términos y condiciones. Debe ser mayor de 18 años para participar.
              </p>
            </div>
          </div>

          {/* Right Content - IMAGEN CON FLYER INTEGRADO */}
          <div className="relative">
            <div className="relative group">
              {/* Glow Effect Mejorado */}
              <div className="absolute -inset-6 bg-gradient-to-r from-secondary/40 to-golden/40 rounded-3xl blur-3xl opacity-70 group-hover:opacity-90 transition-all duration-500"></div>
              
              {/* Main Image Container */}
              <div className="relative bg-gradient-to-br from-card/95 to-card/85 backdrop-blur-md rounded-3xl p-8 border-2 border-golden/40 shadow-2xl">
                {/* LA IMAGEN - PRIORIDAD: flyer-1.jpg, fallback automático */}
                <Image
                  src={displayData.image}
                  alt={displayData?.imageAlt}
                  className="w-full h-96 md:h-[500px] object-cover rounded-2xl shadow-xl"
                  onError={(e) => {
                    console.log('⚠️ Flyer no encontrado, usando fallback automático');
                    e.target.src = displayData.fallbackImage;
                  }}
                  onLoad={() => {
                    setFlyerLoaded(true);
                    console.log('✅ Imagen cargada exitosamente');
                  }}
                />
                
                {/* Overlay Badge Mejorado */}
                <div className="absolute top-6 right-6 bg-gradient-to-r from-secondary to-amber-500 text-gray-900 px-6 py-3 rounded-full shadow-2xl border-2 border-golden/30">
                  <div className="flex items-center space-x-2">
                    <Icon name="Star" size={20} />
                    <span className="font-heading font-bold text-lg">
                      {flyerLoaded ? 'Premio Principal' : 'Imagen del Sorteo'}
                    </span>
                  </div>
                </div>

                {/* Bottom Info Mejorada */}
                <div className="absolute bottom-6 left-6 right-6 bg-gradient-to-r from-background/95 to-card/95 backdrop-blur-sm rounded-2xl p-6 border border-golden/30 shadow-xl">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <h3 className="font-heading font-bold text-2xl text-foreground">
                        {flyerLoaded ? 'Flyer Promocional' : 'Gran Premio'}
                      </h3>
                      <p className="text-base text-text-secondary font-medium">
                        {displayData?.drawDate}
                      </p>
                      <p className="text-sm text-text-secondary">
                        {availableTickets.toLocaleString()} boletos disponibles
                      </p>
                    </div>
                    <div className="text-right space-y-1">
                      <div className="text-3xl font-heading font-bold text-secondary">
                        {displayData?.mainPrize}
                      </div>
                      <div className="text-sm text-text-secondary font-medium">
                        Por boleto: Bs. {displayData?.ticketPrice?.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Elements Animados */}
            <div className="absolute -top-8 -left-8 w-16 h-16 bg-secondary/30 rounded-full blur-2xl animate-pulse"></div>
            <div className="absolute -bottom-8 -right-8 w-20 h-20 bg-golden/30 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            <div className="absolute top-1/2 -left-12 w-8 h-8 bg-secondary/20 rounded-full blur-xl animate-pulse" style={{ animationDelay: '2s' }}></div>
          </div>
        </div>
      </div>
      
      {/* Scroll Indicator Mejorado */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
        <div className="flex flex-col items-center space-y-3 text-text-secondary animate-bounce">
          <span className="text-sm font-medium bg-card/50 rounded-full px-3 py-1">Desliza para ver más</span>
          <Icon name="ChevronDown" size={24} className="text-secondary" />
        </div>
      </div>
    </div>
  );
};

export default HeroBanner;